import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { doc, onSnapshot } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { db } from '@/js/firebase'
import {
  storageSubscriptionService,
  STORAGE_TIERS,
  STORAGE_ADDONS,
  isPlatformSubscription,
  resolvePlatformAllowance,
  billableGB,
  storageCharge,
  rateSummary
} from '@/services/storageSubscriptionService'
import { storageUsageService } from '@/services/storageUsageService'
import { logger } from '@/services/loggerService'

export const useStorageSubscriptionStore = defineStore('storageSubscription', () => {
  // State
  const subscriptions = ref(new Map()) // companyId -> subscription
  const usageCache = ref(new Map()) // companyId -> usage breakdown
  const usageListeners = ref(new Map()) // companyId -> Firestore unsubscribe fn
  const tierOptions = ref(null)
  const isLoading = ref(false)
  const loadingStates = ref(new Map()) // companyId -> loading state
  const error = ref(null)

  // Getters
  const getSubscriptionByCompany = computed(() => (companyId) => {
    return subscriptions.value.get(companyId) || null
  })

  const getUsageByCompany = computed(() => (companyId) => {
    return usageCache.value.get(companyId) || null
  })

  const isCompanyLoading = computed(() => (companyId) => {
    return loadingStates.value.get(companyId) || false
  })

  // Computed for current subscription context
  const currentTier = computed(() => (companyId) => {
    const sub = subscriptions.value.get(companyId)
    return sub?.tier || 'basic'
  })

  const usagePercentage = computed(() => (companyId) => {
    const usage = usageCache.value.get(companyId)
    return usage?.percentUsed || 0
  })

  const isOverLimit = computed(() => (companyId) => {
    const usage = usageCache.value.get(companyId)
    return usage?.status === 'overage'
  })

  const effectiveLimit = computed(() => (companyId) => {
    const sub = subscriptions.value.get(companyId)
    if (!sub) return 0
    return storageSubscriptionService.calculateEffectiveLimit(sub)
  })

  const isQualified = computed(() => (companyId) => {
    const sub = subscriptions.value.get(companyId)
    return !!sub?.qualifiedBy || !!sub?.adminOverride
  })

  const gracePeriodStatus = computed(() => (companyId) => {
    const sub = subscriptions.value.get(companyId)
    if (!sub?.gracePeriod) return null

    // The live listener merges raw Firestore data, so these may be Timestamps.
    const toMs = (v) => (v?.toMillis ? v.toMillis() : v ?? null)
    const now = Date.now()
    const endsAt = toMs(sub.gracePeriod.endsAt)
    const daysRemaining = Math.ceil((endsAt - now) / (1000 * 60 * 60 * 24))
    const startedAt = toMs(sub.gracePeriod.startedAt)

    return {
      type: sub.gracePeriod.type,
      reason: sub.gracePeriod.reason || null,
      daysRemaining: Math.max(0, daysRemaining),
      // Day N since the grace started (0-based, matches server milestones)
      daysElapsed: startedAt ? Math.max(0, Math.floor((now - startedAt) / (1000 * 60 * 60 * 24))) : null,
      startedAt,
      endsAt,
      expired: sub.gracePeriod.expired === true,
      notificationsSent: sub.gracePeriod.notificationsSent || [],
      previousTier: sub.gracePeriod.previousTier
    }
  })

  // --- Vysual Platform (metered) plan ---

  const isMetered = computed(() => (companyId) => {
    return isPlatformSubscription(subscriptions.value.get(companyId))
  })

  /** { feeExempt, qualifies, allowanceBytes } — null when not on the platform plan */
  const platformAllowance = computed(() => (companyId) => {
    const sub = subscriptions.value.get(companyId)
    if (!isPlatformSubscription(sub)) return null
    return resolvePlatformAllowance(sub, storageSubscriptionService.getPlatformTier())
  })

  /**
   * Running monthly estimate for a platform account, from current usage:
   * (base plan ? fee : 0) + billableGB × rate. Metering bills the average,
   * so this is indicative only.
   */
  const platformEstimate = computed(() => (companyId) => {
    const sub = subscriptions.value.get(companyId)
    if (!isPlatformSubscription(sub)) return null
    const platformTier = storageSubscriptionService.getPlatformTier()
    const { feeExempt, allowanceBytes } = resolvePlatformAllowance(sub, platformTier)
    const currentBytes = usageCache.value.get(companyId)?.currentBytes ?? sub.usageStats?.currentBytes ?? 0
    const gb = billableGB(currentBytes, allowanceBytes)
    const fee = feeExempt ? 0 : (platformTier.pricePerMonth ?? 99)
    const usageCost = storageCharge(gb, platformTier)
    return {
      fee,
      rate: platformTier.overageRatePerGB ?? 0.2,
      rateLabel: rateSummary(platformTier),
      billableGB: gb,
      usageCost,
      total: fee + usageCost
    }
  })

  /** Active (unexpired) billing_required grace on the contract */
  const isBillingRequired = computed(() => (companyId) => {
    const grace = subscriptions.value.get(companyId)?.gracePeriod
    return grace?.type === 'billing_required' && grace.expired !== true
  })

  /** Suspended for missing billing (billing_required grace expired) */
  const isBillingSuspended = computed(() => (companyId) => {
    const sub = subscriptions.value.get(companyId)
    return sub?.suspension?.suspended === true && sub.suspension.reason === 'billing_required'
  })

  /** Has a Stripe subscription — offer the customer portal, not Checkout */
  // A canceled subscription leaves its id behind but no longer pays — that
  // account needs a fresh checkout, not the portal. Mirrors the server's
  // LIVE_STRIPE_STATUSES.
  const LIVE_STRIPE_STATUSES = ['active', 'trialing', 'past_due']
  const hasStripeSubscription = computed(() => (companyId) => {
    const sub = subscriptions.value.get(companyId)
    return !!sub?.stripeSubscriptionId && LIVE_STRIPE_STATUSES.includes(sub.stripeStatus)
  })

  const isSuspended = computed(() => (companyId) => {
    const sub = subscriptions.value.get(companyId)
    if (!sub) return false
    return sub.suspension?.suspended === true
  })

  const isAccessBlocked = computed(() => (companyId) => {
    const sub = subscriptions.value.get(companyId)
    if (!sub) return false

    // No grace period means no restrictions
    if (!sub.gracePeriod) return false

    // Check if explicitly marked as expired
    if (sub.gracePeriod.expired === true) return true

    // Check if uploads are explicitly blocked
    if (sub.gracePeriod.uploadsBlocked === true) return true

    // Check if grace period end date has passed
    const now = Date.now()
    if (sub.gracePeriod.endsAt && now >= sub.gracePeriod.endsAt) return true

    return false
  })

  // Actions
  const loadSubscription = async (companyId, options = {}) => {
    const { force = false } = options

    try {
      // Return cached if available and not forcing refresh
      if (!force && subscriptions.value.has(companyId)) {
        return subscriptions.value.get(companyId)
      }

      loadingStates.value.set(companyId, true)
      error.value = null

      const subscription = await storageSubscriptionService.getSubscription(companyId)

      if (subscription) {
        subscriptions.value.set(companyId, subscription)
      }

      logger.info('[StorageSubscriptionStore] Subscription loaded', {
        companyId,
        found: !!subscription,
        tier: subscription?.tier
      })

      return subscription
    } catch (err) {
      error.value = err.message
      logger.error('[StorageSubscriptionStore] Failed to load subscription', {
        error: err.message,
        companyId
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const loadUsage = async (companyId, options = {}) => {
    const { force = false } = options

    try {
      // Return cached if available and not forcing refresh
      if (!force && usageCache.value.has(companyId)) {
        return usageCache.value.get(companyId)
      }

      loadingStates.value.set(companyId, true)

      const usage = await storageUsageService.getUsageBreakdown(companyId)
      usageCache.value.set(companyId, usage)

      logger.info('[StorageSubscriptionStore] Usage loaded', {
        companyId,
        currentBytes: usage.currentBytes,
        percentUsed: usage.percentUsed?.toFixed(2)
      })

      return usage
    } catch (err) {
      logger.error('[StorageSubscriptionStore] Failed to load usage', {
        error: err.message,
        companyId
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  // Live subscription to the company's active storage contract — keeps
  // usageCache.currentBytes / percentUsed in sync with server-side updates
  // (Bunny webhook, PDF thumbnail trigger, nightly sync) without requiring
  // the storage tab to refetch.
  const subscribeUsage = async (companyId) => {
    if (!companyId) return
    if (usageListeners.value.has(companyId)) return

    let subscription = subscriptions.value.get(companyId)
    if (!subscription) {
      subscription = await loadSubscription(companyId)
    }
    if (!subscription?.id) return

    const unsubscribe = onSnapshot(
      doc(db, 'contracts', subscription.id),
      (snap) => {
        if (!snap.exists()) return
        const data = snap.data()
        const stats = data.usageStats || {}

        // Re-set the cache entry so the Map ref triggers reactivity.
        // Recompute `status` locally so isOverLimit / banners flip the
        // moment the server publishes a new percentUsed — the server-set
        // status field is authoritative but the listener fires before the
        // evaluator on some paths, and we don't want UI to lag.
        const existing = usageCache.value.get(companyId) || {}
        const nextPercentUsed = typeof stats.percentUsed === 'number'
          ? stats.percentUsed
          : existing.percentUsed || 0
        usageCache.value.set(companyId, {
          ...existing,
          currentBytes: stats.currentBytes || 0,
          percentUsed: nextPercentUsed,
          // Platform status is recomputed: a migrated contract can still carry
          // a stale legacy 'overage' until the server next evaluates it.
          status: isPlatformSubscription(data)
            ? storageSubscriptionService.getUsageStatusFor(data, nextPercentUsed)
            : (stats.status || storageSubscriptionService.getUsageStatusFor(data, nextPercentUsed)),
          lastUpdatedAt: stats.lastUpdatedAt || existing.lastUpdatedAt,
          lastSyncedAt: stats.lastSyncedAt || existing.lastSyncedAt,
        })

        // Mirror to the subscription cache so getSubscriptionByCompany sees
        // fresh stats too.
        subscriptions.value.set(companyId, { ...subscription, ...data, id: subscription.id })

        logger.debug('[StorageSubscriptionStore] Live usage update', {
          companyId, currentBytes: stats.currentBytes,
        })
      },
      (err) => {
        logger.error('[StorageSubscriptionStore] Usage listener error', {
          companyId, error: err.message,
        })
      }
    )

    usageListeners.value.set(companyId, unsubscribe)
  }

  const unsubscribeUsage = (companyId) => {
    const unsub = usageListeners.value.get(companyId)
    if (unsub) {
      unsub()
      usageListeners.value.delete(companyId)
    }
  }

  const loadTierOptions = async () => {
    try {
      if (tierOptions.value) return tierOptions.value

      isLoading.value = true
      tierOptions.value = await storageSubscriptionService.getTierOptions()

      logger.info('[StorageSubscriptionStore] Tier options loaded', {
        tierCount: tierOptions.value.tiers?.length,
        addonCount: tierOptions.value.addOns?.length
      })

      return tierOptions.value
    } catch (err) {
      logger.error('[StorageSubscriptionStore] Failed to load tier options', {
        error: err.message
      })
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const createSubscription = async (companyId, tier = 'basic', qualifiedBy = null, options = {}) => {
    try {
      loadingStates.value.set(companyId, true)
      error.value = null

      const subscription = await storageSubscriptionService.createSubscription(
        companyId,
        tier,
        qualifiedBy,
        options
      )

      subscriptions.value.set(companyId, subscription)

      // Also load initial usage
      await loadUsage(companyId, { force: true })

      logger.info('[StorageSubscriptionStore] Subscription created', {
        companyId,
        subscriptionId: subscription.id,
        tier
      })

      return subscription
    } catch (err) {
      error.value = err.message
      logger.error('[StorageSubscriptionStore] Failed to create subscription', {
        error: err.message,
        companyId,
        tier
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const changeTier = async (companyId, newTier) => {
    try {
      const subscription = subscriptions.value.get(companyId)
      if (!subscription) {
        throw new Error('No subscription found for company')
      }

      loadingStates.value.set(companyId, true)
      error.value = null

      const updated = await storageSubscriptionService.changeTier(subscription.id, newTier)
      subscriptions.value.set(companyId, updated)

      // Refresh usage to get updated percentages
      await loadUsage(companyId, { force: true })

      logger.info('[StorageSubscriptionStore] Tier changed', {
        companyId,
        newTier
      })

      return updated
    } catch (err) {
      error.value = err.message
      logger.error('[StorageSubscriptionStore] Failed to change tier', {
        error: err.message,
        companyId,
        newTier
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const addAddon = async (companyId, addonType) => {
    try {
      const subscription = subscriptions.value.get(companyId)
      if (!subscription) {
        throw new Error('No subscription found for company')
      }

      loadingStates.value.set(companyId, true)
      error.value = null

      const updated = await storageSubscriptionService.addAddon(subscription.id, addonType)
      subscriptions.value.set(companyId, updated)

      // Refresh usage to get updated percentages
      await loadUsage(companyId, { force: true })

      logger.info('[StorageSubscriptionStore] Add-on added', {
        companyId,
        addonType
      })

      return updated
    } catch (err) {
      error.value = err.message
      logger.error('[StorageSubscriptionStore] Failed to add add-on', {
        error: err.message,
        companyId,
        addonType
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const removeAddon = async (companyId, addonId) => {
    try {
      const subscription = subscriptions.value.get(companyId)
      if (!subscription) {
        throw new Error('No subscription found for company')
      }

      loadingStates.value.set(companyId, true)
      error.value = null

      const updated = await storageSubscriptionService.removeAddon(subscription.id, addonId)
      subscriptions.value.set(companyId, updated)

      // Refresh usage to get updated percentages
      await loadUsage(companyId, { force: true })

      logger.info('[StorageSubscriptionStore] Add-on removed', {
        companyId,
        addonId
      })

      return updated
    } catch (err) {
      error.value = err.message
      logger.error('[StorageSubscriptionStore] Failed to remove add-on', {
        error: err.message,
        companyId,
        addonId
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const refreshUsage = async (companyId) => {
    return loadUsage(companyId, { force: true })
  }

  const remediateStuckOverageGrace = async (options = {}) => {
    // Admin-only callable. Sweeps active storage contracts whose
    // gracePeriod is type='overage' AND expired===true, then runs
    // evaluateStorageState on each. Pass { dryRun: true } to preview.
    const fn = httpsCallable(getFunctions(), 'remediateStuckOverageGrace')
    const { data } = await fn(options)
    logger.info('[StorageSubscriptionStore] Stuck grace remediation', {
      dryRun: !!options.dryRun,
      evaluated: data.evaluated,
      cleared: data.cleared,
      restarted: data.restarted,
    })
    return data
  }

  const requalify = async (companyId) => {
    // Admin-only callable. Re-runs the canonical contract-qualification logic
    // (same as the onProductionContractChange trigger) then reconciles usage
    // state. Use this to correct a subscription stuck below its qualified tier
    // — NOT a manual override.
    const fn = httpsCallable(getFunctions(), 'requalifyCompanyStorage')
    const { data } = await fn({ companyId })
    logger.info('[StorageSubscriptionStore] Re-qualified company', {
      companyId,
      tier: data.tier,
      status: data.status
    })
    await loadSubscription(companyId, { force: true })
    return data
  }

  const recalculateUsage = async (companyId) => {
    try {
      loadingStates.value.set(companyId, true)

      // Server-side deep refresh: re-fetches Bunny Stream `storageSize` per
      // video before summing, then writes both company snapshot + contract
      // usageStats. Catches drift from late-arriving variants or missed
      // webhooks that the client-side sum alone would miss.
      const fn = httpsCallable(getFunctions(), 'refreshCompanyAssetSizes')
      const { data: result } = await fn({ companyId })

      const currentUsage = usageCache.value.get(companyId) || {}
      usageCache.value.set(companyId, {
        ...currentUsage,
        currentBytes: result.currentBytes,
      })

      logger.info('[StorageSubscriptionStore] Usage recalculated (deep refresh)', {
        companyId,
        currentBytes: result.currentBytes,
        assetCount: result.assetCount,
        bunnyAssetCount: result.bunnyAssetCount,
        refreshed: result.refreshed,
        updated: result.updated,
        errors: result.errors,
      })

      return result
    } catch (err) {
      logger.error('[StorageSubscriptionStore] Failed to recalculate usage', {
        error: err.message,
        companyId
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const updateLocalUsage = (companyId, bytes, isIncrement = true) => {
    // Optimistically update local cache
    const usage = usageCache.value.get(companyId)
    if (usage) {
      const newBytes = isIncrement
        ? usage.currentBytes + bytes
        : Math.max(0, usage.currentBytes - bytes)

      const newPercentUsed = usage.effectiveLimit > 0
        ? (newBytes / usage.effectiveLimit) * 100
        : 0

      usageCache.value.set(companyId, {
        ...usage,
        currentBytes: newBytes,
        percentUsed: newPercentUsed,
        status: storageSubscriptionService.getUsageStatusFor(
          subscriptions.value.get(companyId),
          newPercentUsed
        )
      })

      // If this drop takes us back below 100% and the cached subscription
      // shows an active overage grace, clear it locally to mirror what the
      // server-side onAssetDeletedSyncStorage trigger will do. Without this,
      // the banner / upload guard / approve-button stay stuck for users on
      // pages that don't have an active subscribeUsage listener (anywhere
      // outside the storage tabs).
      if (!isIncrement && newPercentUsed < 100) {
        const sub = subscriptions.value.get(companyId)
        if (sub?.gracePeriod?.type === 'overage') {
          subscriptions.value.set(companyId, { ...sub, gracePeriod: null })
        }
      }
    }
  }

  const createCheckoutSession = async (companyId, options) => {
    try {
      loadingStates.value.set(companyId, true)

      const result = await storageSubscriptionService.createCheckoutSession(companyId, options)

      logger.info('[StorageSubscriptionStore] Checkout session created', {
        companyId,
        sessionId: result.sessionId
      })

      return result
    } catch (err) {
      logger.error('[StorageSubscriptionStore] Failed to create checkout session', {
        error: err.message,
        companyId
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  /**
   * "Add billing information" for the platform plan. Redirects to Stripe
   * Checkout, or to the customer portal once a subscription exists.
   * Returns the URL it redirected to.
   */
  const startPlatformBilling = async (companyId, { successUrl, cancelUrl } = {}) => {
    try {
      loadingStates.value.set(companyId, true)
      const sub = subscriptions.value.get(companyId)

      // The portal's return URL: the storage tab without the checkout toast param.
      const portalReturnUrl = cancelUrl ? cancelUrl.replace(/[&?]checkout=[^&]*/, '') : null
      let url
      let via = 'checkout'
      if (sub?.stripeSubscriptionId && LIVE_STRIPE_STATUSES.includes(sub.stripeStatus)) {
        via = 'portal'
        url = await storageSubscriptionService.getCustomerPortalUrl(companyId, portalReturnUrl)
      } else {
        try {
          const result = await storageSubscriptionService.createPlatformCheckout(companyId, { successUrl, cancelUrl })
          url = result?.url
        } catch (err) {
          // Server says a subscription already exists → manage it in the portal.
          if (err?.details?.usePortal) {
            via = 'portal'
            url = await storageSubscriptionService.getCustomerPortalUrl(companyId, portalReturnUrl)
          } else {
            throw err
          }
        }
      }

      logger.info('[StorageSubscriptionStore] Platform billing started', { companyId, via })

      if (url) window.location.href = url
      return url
    } catch (err) {
      logger.error('[StorageSubscriptionStore] Failed to start platform billing', {
        error: err.message,
        companyId
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  /** Admin: set (bytes) or clear (null → base plan) the platform allowance */
  const updateAllowance = async (companyId, allowanceBytes) => {
    try {
      loadingStates.value.set(companyId, true)
      error.value = null

      const updated = await storageSubscriptionService.updateAllowance(companyId, allowanceBytes)
      subscriptions.value.set(companyId, updated)

      await loadUsage(companyId, { force: true })

      logger.info('[StorageSubscriptionStore] Platform allowance updated', { companyId, allowanceBytes })
      return updated
    } catch (err) {
      error.value = err.message
      logger.error('[StorageSubscriptionStore] Failed to update allowance', {
        error: err.message,
        companyId
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const getCustomerPortalUrl = async (companyId) => {
    try {
      const url = await storageSubscriptionService.getCustomerPortalUrl(companyId)
      return url
    } catch (err) {
      logger.error('[StorageSubscriptionStore] Failed to get customer portal URL', {
        error: err.message,
        companyId
      })
      throw err
    }
  }

  const ensureStripeCustomer = async (companyId) => {
    // Fire and forget - pre-create Stripe customer for faster checkout
    // Don't block on this, just log result
    storageSubscriptionService.ensureStripeCustomer(companyId)
  }

  const applyDiscount = async (companyId, discount) => {
    try {
      const subscription = subscriptions.value.get(companyId)
      if (!subscription) {
        throw new Error('No subscription found for company')
      }

      loadingStates.value.set(companyId, true)
      error.value = null

      const updated = await storageSubscriptionService.applyDiscount(subscription.id, discount)
      subscriptions.value.set(companyId, updated)

      logger.info('[StorageSubscriptionStore] Discount applied', {
        companyId,
        discountType: discount.type,
        discountValue: discount.value
      })

      return updated
    } catch (err) {
      error.value = err.message
      logger.error('[StorageSubscriptionStore] Failed to apply discount', {
        error: err.message,
        companyId
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const removeDiscount = async (companyId) => {
    try {
      const subscription = subscriptions.value.get(companyId)
      if (!subscription) {
        throw new Error('No subscription found for company')
      }

      loadingStates.value.set(companyId, true)
      error.value = null

      const updated = await storageSubscriptionService.removeDiscount(subscription.id)
      subscriptions.value.set(companyId, updated)

      logger.info('[StorageSubscriptionStore] Discount removed', { companyId })

      return updated
    } catch (err) {
      error.value = err.message
      logger.error('[StorageSubscriptionStore] Failed to remove discount', {
        error: err.message,
        companyId
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const setAdminOverride = async (companyId, tier, reason) => {
    try {
      loadingStates.value.set(companyId, true)
      error.value = null

      const updated = await storageSubscriptionService.setAdminOverride(companyId, tier, reason)
      subscriptions.value.set(companyId, updated)

      await loadUsage(companyId, { force: true })

      logger.info('[StorageSubscriptionStore] Admin override set', {
        companyId,
        tier,
        reason
      })

      return updated
    } catch (err) {
      error.value = err.message
      logger.error('[StorageSubscriptionStore] Failed to set admin override', {
        error: err.message,
        companyId,
        tier
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const removeAdminOverride = async (companyId) => {
    try {
      loadingStates.value.set(companyId, true)
      error.value = null

      const updated = await storageSubscriptionService.removeAdminOverride(companyId)
      subscriptions.value.set(companyId, updated)

      await loadUsage(companyId, { force: true })

      logger.info('[StorageSubscriptionStore] Admin override removed', { companyId })

      return updated
    } catch (err) {
      error.value = err.message
      logger.error('[StorageSubscriptionStore] Failed to remove admin override', {
        error: err.message,
        companyId
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const setSuspension = async (companyId, suspended) => {
    try {
      loadingStates.value.set(companyId, true)
      error.value = null

      const updated = await storageSubscriptionService.setSuspension(companyId, suspended)
      subscriptions.value.set(companyId, updated)

      logger.info('[StorageSubscriptionStore] Suspension toggled', { companyId, suspended })
      return updated
    } catch (err) {
      error.value = err.message
      logger.error('[StorageSubscriptionStore] Failed to set suspension', {
        error: err.message,
        companyId
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const setSuspensionExempt = async (companyId, exempt) => {
    try {
      loadingStates.value.set(companyId, true)
      error.value = null

      const updated = await storageSubscriptionService.setSuspensionExempt(companyId, exempt)
      subscriptions.value.set(companyId, updated)

      logger.info('[StorageSubscriptionStore] Suspension exemption toggled', { companyId, exempt })
      return updated
    } catch (err) {
      error.value = err.message
      logger.error('[StorageSubscriptionStore] Failed to set suspension exempt', {
        error: err.message,
        companyId
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const calculateEffectivePrice = (companyId) => {
    const subscription = subscriptions.value.get(companyId)
    return storageSubscriptionService.calculateEffectivePrice(subscription)
  }

  const unlinkStripeBilling = async (companyId) => {
    try {
      loadingStates.value.set(companyId, true)
      error.value = null

      const updated = await storageSubscriptionService.unlinkStripeBilling(companyId)
      subscriptions.value.set(companyId, updated)

      logger.info('[StorageSubscriptionStore] Stripe billing unlinked', { companyId })

      return updated
    } catch (err) {
      error.value = err.message
      logger.error('[StorageSubscriptionStore] Failed to unlink Stripe billing', {
        error: err.message,
        companyId
      })
      throw err
    } finally {
      loadingStates.value.set(companyId, false)
    }
  }

  const clearCache = (companyId = null) => {
    if (companyId) {
      subscriptions.value.delete(companyId)
      usageCache.value.delete(companyId)
      loadingStates.value.delete(companyId)
    } else {
      subscriptions.value.clear()
      usageCache.value.clear()
      loadingStates.value.clear()
      tierOptions.value = null
    }
    error.value = null
    logger.info('[StorageSubscriptionStore] Cache cleared', { companyId })
  }

  // Helper to get tier display info
  const getTierInfo = (tier) => {
    return STORAGE_TIERS[tier] || null
  }

  // Helper to get addon display info
  const getAddonInfo = (addonType) => {
    return STORAGE_ADDONS[addonType] || null
  }

  // Helper to format bytes
  const formatBytes = (bytes) => {
    return storageSubscriptionService.formatBytes(bytes)
  }

  return {
    // State
    subscriptions,
    usageCache,
    tierOptions,
    isLoading,
    loadingStates,
    error,

    // Getters
    getSubscriptionByCompany,
    getUsageByCompany,
    isCompanyLoading,
    currentTier,
    usagePercentage,
    isOverLimit,
    effectiveLimit,
    isQualified,
    gracePeriodStatus,
    isAccessBlocked,
    isSuspended,
    isMetered,
    platformAllowance,
    platformEstimate,
    isBillingRequired,
    isBillingSuspended,
    hasStripeSubscription,

    // Actions
    loadSubscription,
    loadUsage,
    subscribeUsage,
    unsubscribeUsage,
    loadTierOptions,
    createSubscription,
    changeTier,
    addAddon,
    removeAddon,
    refreshUsage,
    recalculateUsage,
    requalify,
    remediateStuckOverageGrace,
    updateLocalUsage,
    createCheckoutSession,
    getCustomerPortalUrl,
    startPlatformBilling,
    updateAllowance,
    ensureStripeCustomer,
    applyDiscount,
    removeDiscount,
    setAdminOverride,
    removeAdminOverride,
    setSuspension,
    setSuspensionExempt,
    unlinkStripeBilling,
    clearCache,

    // Helpers
    getTierInfo,
    getAddonInfo,
    formatBytes,
    calculateEffectivePrice
  }
})
